import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatRadioChange } from '@angular/material/radio';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { clone, cloneDeep, isEmpty } from 'lodash';
import moment from 'moment';
import { forkJoin } from 'rxjs';
import { filter, finalize, skipWhile, take } from 'rxjs/operators';

import { DocumentService } from '../../../core/api/document.service';
import { GisService } from '../../../core/api/gis.service';
import { SiteService } from '../../../core/api/site.service';
import { UserService } from '../../../core/api/user.service';
import { FileService } from '../../../core/helper/file.service';
import { FormatHelperService } from '../../../core/helper/format-helper.service';
import { MapService } from '../../../core/helper/map.service';
import { SiteHelperService } from '../../../core/helper/site.service';
import { SiteReviewCheckEnums } from '../../../shared/constants/site_review_check.enums';
import { SiteReviewNoteEnums } from '../../../shared/constants/site_review_note.enums';
import { StatusEnum } from '../../../shared/constants/site_status.enum';
import { DialogEditParcelDataComponent } from '../../../shared/dialogs/dialog-edit-parcel-data/dialog-edit-parcel-data.component';
import { DialogResubmitNoteComponent } from '../../../shared/dialogs/dialog-resubmit-note/dialog-resubmit-note.component';
import { DialogYesNoComponent } from '../../../shared/dialogs/dialog-yes-no/dialog-yes-no.component';
import { Document, DocumentType, Parcel, Program, Site, SiteReviewCheck, SiteReviewNote } from '../../../shared/models';
import { SpinnerService } from '../../../shared/spinner/spinner.service';
import { selectAppConfigState } from '../../../store/app-config/app-config.selectors';
import { selectDocumentTypes } from '../../../store/document/document.selectors';
import * as fromApp from '../../../store/index';
import { setActiveSiteSuccess } from '../../../store/site/site.actions';
import { selectActiveSite } from '../../../store/site/site.selectors';
import { EditProgramsDialogComponent } from '../dialog-edit-programs/dialog-edit-programs.component';

interface ForestOrLandUseProgram {
  restrictionLevel: number;
  type: string;
  name: string;
  note?: string;
}

interface Note {
  id: number;
  note: string;
  type: SiteReviewNoteEnums;
  siteId: number;
}

@Component({
  selector: 'fc-site-approval-step-1',
  templateUrl: './site-approval-step-1.component.html',
  styleUrls: ['./site-approval-step-1.component.scss'],
})
export class SiteApprovalStep1Component implements OnInit {
  @Input()
  isSuperAdmin = false;

  @Input()
  expanded = false;

  @Output()
  doChangeSiteState: EventEmitter<StatusEnum> = new EventEmitter<StatusEnum>();

  destroyRef = inject(DestroyRef);
  displayedColumns: string[] = ['parcelNum', 'owner', 'county', 'state'];

  site: Site;
  errorMessage: string;
  valuation: string;
  parcelData: Parcel[] = [];

  docTypes = [];
  redflagDetails = [];
  documents: Document[] = null;
  leakageDocs: Document[] = null;
  flaggedProfile: boolean = false;

  siteOverviewNoteForm: UntypedFormGroup;
  identityVerificationNoteForm: UntypedFormGroup;
  parcelOwnershipNoteForm: UntypedFormGroup;
  siteCheckNoteForm: UntypedFormGroup;
  supportingDocumentsNoteForm: UntypedFormGroup;
  otherNoteForm: UntypedFormGroup;
  updatesForm: UntypedFormGroup;
  redflagMap = [];

  ownerNamesAndParcelIDsVerifiedCheck?: SiteReviewCheck;
  googleEarthEngineChangeDetectionVerifiedCheck?: SiteReviewCheck;
  restrictedCovenantsCheck?: SiteReviewCheck;
  mapApproved?: SiteReviewCheck;
  allSupportingDocumentsProvided?: SiteReviewCheck;
  buyerSpecificRequirementDocument?: SiteReviewCheck;
  possibleValues = ['Yes', 'No', 'Deferred'];

  constructor(
    public dialog: MatDialog,
    private formBuilder: UntypedFormBuilder,
    readonly siteHelper: SiteHelperService,
    readonly userService: UserService,
    readonly spinnerService: SpinnerService,
    readonly siteService: SiteService,
    readonly gisService: GisService,
    readonly documentService: DocumentService,
    readonly formatHelper: FormatHelperService,
    readonly fileService: FileService,
    readonly mapHelper: MapService,
    readonly router: Router,
    readonly store: Store<fromApp.AppState>
  ) {}

  ngOnInit() {
    this.store
      .select(selectAppConfigState)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(appConfig => {
        if (typeof appConfig.config['redflag_map'] !== 'undefined') {
          this.redflagMap = JSON.parse(appConfig.config['redflag_map']);
        }
      });
    this.store
      .select(selectActiveSite)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        skipWhile(site => site === null)
      )
      .subscribe(site => {
        this.site = site;
        this.valuation = this.site.valuation ? this.site.valuation.split(',')[0] : '0';
        this.flaggedProfile = this.site.account.user.flaggedProfile;
        this.parcelData = cloneDeep(this.site.versions[0].parcels);
        this.parcelData.sort((a, b) => a.parcelNumber.localeCompare(b.parcelNumber));
        this.updatesForm = this.formBuilder.group({
          initialAdjustment: this.site.initialAdjustment,
        });

        this.setSiteReviewNotes();
        this.setSiteReviewChecks();

        this.gisService.getRedflagOverlapDetails(this.site.id).subscribe((overlapDetails: any) => {
          this.redflagDetails = overlapDetails;
        });

        forkJoin([
          this.store.select('document', 'documents').pipe(
            skipWhile(docs => docs === null),
            take(1)
          ),
          this.store.select(selectDocumentTypes).pipe(
            skipWhile(docTypes => isEmpty(docTypes)),
            take(1)
          ),
        ])
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(([docs, docTypes]: [any[], DocumentType[]]) => {
            this.docTypes = docTypes;
            let leakageDocTypeId = this.docTypes.find((dt: DocumentType) => dt.name === 'Leakage').id;
            let propertyDocNames = ['Tax Bill', 'Deed', 'Survey', 'Title Report'];
            if (typeof docs[this.site.id] !== 'undefined') {
              this.documents = docs[this.site.id].filter(doc =>
                propertyDocNames.includes(this.docTypes.find(dt => dt.id == doc.documentTypeId).name)
              );
              this.leakageDocs = docs[this.site.id].filter(doc => doc.documentTypeId == leakageDocTypeId);
            }
          });
      });
  }

  cloneSiteReviewNote(type: SiteReviewNoteEnums) {
    const note = cloneDeep(
      this.site.siteReviewNotes?.find((prn: SiteReviewNote) => prn.type === type) ||
      new SiteReviewNote({ type, siteId: this.site.id })
    );
    return this.formBuilder.group({
      id: note.id,
      note: note.note,
      type: note.type,
      siteId: note.siteId,
    });
  }

  createSiteReviewCheck(type: SiteReviewCheckEnums) {
    return (
      this.site.siteReviewChecks?.find((prc: SiteReviewCheck) => prc.type === type) ||
      new SiteReviewCheck({
        type,
        checked: false,
        siteId: this.site.id,
      })
    );
  }

  setSiteReviewNotes() {
    this.siteOverviewNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.SiteOverview);
    this.identityVerificationNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.IdentityVerification);
    this.parcelOwnershipNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.ParcelOwnership);
    this.siteCheckNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.SiteCheck);
    this.supportingDocumentsNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.SupportingDocuments);
    this.otherNoteForm = this.cloneSiteReviewNote(SiteReviewNoteEnums.Other);
  }

  setSiteReviewChecks() {
    this.ownerNamesAndParcelIDsVerifiedCheck = this.createSiteReviewCheck(
      SiteReviewCheckEnums.OwnerNamesAndParcelIDsVerified
    );
    this.googleEarthEngineChangeDetectionVerifiedCheck = this.createSiteReviewCheck(
      SiteReviewCheckEnums.GoogleEarthEngineChangeDetectionVerified
    );
    this.restrictedCovenantsCheck = this.createSiteReviewCheck(SiteReviewCheckEnums.RestrictedConvenants);
    this.mapApproved = this.createSiteReviewCheck(SiteReviewCheckEnums.MapApproved);
    this.allSupportingDocumentsProvided = this.createSiteReviewCheck(
      SiteReviewCheckEnums.AllSupportingDocumentsProvided
    );
    this.buyerSpecificRequirementDocument = this.createSiteReviewCheck(
      SiteReviewCheckEnums.BuyerSpecificRequirementDocument
    );
  }

  onRequestMapRevisions(): void {
    this.dialog
      .open(DialogResubmitNoteComponent)
      .afterClosed()
      .subscribe(note => {
        if (note) {
          this.sendResubmitNote(note);
          this.doChangeSiteState.emit(StatusEnum.REQUIRES_LANDOWNER_REVISION);
        }
      });
  }

  onApproveMap(): void {
    this.dialog
      .open(DialogYesNoComponent, {
        panelClass: 'fc-yes-no-dialog',
        data: {
          title: 'Please Confirm',
          line1: 'This will update the site status. Are you sure you want to continue?',
          line2: '',
          buttonTextYes: 'Yes, Approve',
          buttonTextNo: 'No, Cancel',
        },
      })
      .afterClosed()
      .subscribe(result => {
        if (result) {
          this.saveInitialAdjustment();
          this.doChangeSiteState.emit(StatusEnum.APPROVED);
        }
      });
  }

  sendResubmitNote(note: string) {
    this.spinnerService.show('app-spinner');
    this.siteService
      .sendNotification(this.site.id, note, StatusEnum.REQUIRES_LANDOWNER_REVISION)
      .pipe(finalize(() => this.spinnerService.hide('app-spinner')))
      .subscribe((site: Site) => (this.site = site));
  }

  saveInitialAdjustment(): void {
    let initialAdjustment = this.updatesForm.get('initialAdjustment').value
      ? this.updatesForm.get('initialAdjustment').value
      : 0;
    if (initialAdjustment > 0) {
      this.siteService.updateInitialAdjustment(this.site.id, initialAdjustment).subscribe((site: Site) => {
        this.store.dispatch(setActiveSiteSuccess({ data: site }));
      });
    }
  }

  editPrograms(): void {
    const dialogRef = this.dialog.open(EditProgramsDialogComponent, {
      panelClass: 'fc-dialog-edit-programs',
      data: {
        regionId: this.site.cohort.region.id,
        programs: this.site.programs,
        other: this.site.otherPrograms,
      },
      autoFocus: false,
    });

    dialogRef
      .afterClosed()
      .pipe(filter(ids => ids))
      .subscribe((data: any) => {
        let programs = data.selectedProgramIds.map((id: number) => new Program({ id }));
        let otherPrograms = data.other;
        this.siteService.updateSiteEligibilityReport(this.site.id, programs, otherPrograms).subscribe((site: Site) => {
          this.store.dispatch(setActiveSiteSuccess({ data: site }));
        });
      });
  }

  saveSiteReviewNote(noteObj: Note) {
    const siteReviewNote = new SiteReviewNote({
      id: noteObj.id,
      note: noteObj.note,
      type: noteObj.type,
      siteId: this.site.id,
    });
    this.spinnerService.show('app-spinner');
    this.siteService.saveSiteReviewNote(siteReviewNote).subscribe((result: Site) => {
      this.site = result;
      this.setSiteReviewNotes();
      this.spinnerService.hide('app-spinner');
    });
  }

  saveSiteReviewCheck($event: MatRadioChange, siteReviewCheck: SiteReviewCheck) {
    this.spinnerService.show('app-spinner');
    const prc = new SiteReviewCheck(siteReviewCheck);
    prc.status = $event.value;

    this.siteService.saveSiteReviewCheck(prc).subscribe((result: Site) => {
      this.site = result;
      this.setSiteReviewChecks();
      this.spinnerService.hide('app-spinner');
    });
  }

  cancelSiteReviewNote(noteObj: Note) {
    if (this.site.siteReviewNotes) {
      const idx = this.site.siteReviewNotes.findIndex((prn: SiteReviewNote) => prn.type === noteObj.type);
      let note = '';
      if (idx > -1) {
        note = clone(this.site.siteReviewNotes[idx].note);
      }
      switch (noteObj.type) {
        case SiteReviewNoteEnums.SiteOverview:
          this.siteOverviewNoteForm.controls.note.setValue(note);
          this.siteOverviewNoteForm.markAsPristine();
          break;
        case SiteReviewNoteEnums.IdentityVerification:
          this.identityVerificationNoteForm.controls.note.setValue(note);
          this.identityVerificationNoteForm.markAsPristine();
          break;
        case SiteReviewNoteEnums.ParcelOwnership:
          this.parcelOwnershipNoteForm.controls.note.setValue(note);
          this.parcelOwnershipNoteForm.markAsPristine();
          break;
        case SiteReviewNoteEnums.SiteCheck:
          this.siteCheckNoteForm.controls.note.setValue(note);
          this.siteCheckNoteForm.markAsPristine();
          break;
        case SiteReviewNoteEnums.SupportingDocuments:
          this.supportingDocumentsNoteForm.controls.note.setValue(note);
          this.supportingDocumentsNoteForm.markAsPristine();
          break;
        case SiteReviewNoteEnums.Other:
          this.otherNoteForm.controls.note.setValue(note);
          this.otherNoteForm.markAsPristine();
          break;
      }
    }
  }

  getCheckClickedByLine(SiteReviewCheck: SiteReviewCheck) {
    return `${SiteReviewCheck.updatedBy.getDisplayName()} on ${moment(SiteReviewCheck.updatedAt).format('MM/DD/YYYY')}`;
  }

  getDocumentTypeName(documentTypeId: number) {
    return this.docTypes.find((docType: DocumentType) => docType.id === documentTypeId)?.name || 'Unknown Type';
  }

  downloadDocument(doc: Document): void {
    this.documentService
      .downloadDocument(this.site.identifier, doc.identifier, doc.name, true)
      .then(uri => window.open(uri, '_blank'));
  }

  get programs(): ForestOrLandUseProgram[] {
    const forestOrLandUsePrograms: ForestOrLandUseProgram[] = this.site.programs
      .filter((program: Program) => program.isActive)
      .map((program: Program) => {
        return {
          restrictionLevel: program.restrictionLevel,
          type: 'Program',
          name: program.name,
        };
      });
    if (this.site.otherPrograms) {
      this.site.otherPrograms.split(',').forEach((name: string) => {
        forestOrLandUsePrograms.push({
          type: 'Other Program',
          name: name.trim(),
          restrictionLevel: -1,
        });
      });
    }
    if (this.site.reportResponses && this.site.reportResponses.length > 0) {
      this.site.reportResponses[0].answers.forEach((answer: any) => {
        if (this.site.hasRestrictions && answer.special_type === 'restrictions') {
          forestOrLandUsePrograms.push({
            type: 'Eligibility Question',
            name: 'Encumbrance',
            note: answer.comments,
            restrictionLevel: 1,
          });
        }
        if (answer.special_type === 'activity_leakage' && answer.answer) {
          forestOrLandUsePrograms.push({
            type: 'Eligibility Question',
            name: 'Leakage',
            note: answer.comments,
            restrictionLevel: 1,
          });
        }
      });
    }
    return forestOrLandUsePrograms;
  }

  get isCurrentStep() {
    return this.site?.approvedDate === null && this.site?.submittedDate !== null;
  }

  get stepComplete() {
    return this.site?.submittedDate !== null && this.site?.approvedDate !== null;
  }

  downloadAdminChecklist(): void {
    this.siteService.downloadChecklist(this.site.id).subscribe(bytes => {
      const blob = this.fileService.getBlobFromBytes(bytes);

      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = 'Site_ID_' + this.site.id + '_Admin_Checklist.docx';
      link.click();
    });
  }

  editParcel(parcel: Parcel) {
    this.dialog.open(DialogEditParcelDataComponent, {
      data: {
        parcel: parcel,
        siteId: this.site.id,
      },
    });
  }
}
